import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Calculator } from "lucide-react";
import { Input } from "./ui/input";
import { Button } from "./ui/button";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MODES = [["levels", "المستويات"], ["formulas", "الصيغ"], ["both", "الاثنين معاً"]];
const DED = { grade: "خصم التقييم", face: "عدم تطابق Face ID", fake_gps: "Fake GPS", rejection: "رفض الطلبات", cancel: "إلغاء الطلبات" };
const F = ({ label, children }) => <div><label className="mb-1 block text-xs text-slate-500">{label}</label>{children}</div>;

const Result = ({ r }) => (
  <div className="space-y-3" data-testid="sim-result">
    <div className="grid gap-3 sm:grid-cols-3">
      {MODES.map(([m, l]) => (
        <div key={m} className={`rounded-2xl border p-4 ${r.current_mode === m ? "border-indigo-400 bg-indigo-50" : "border-slate-200 bg-white"}`} data-testid={`sim-mode-${m}`}>
          <p className="text-xs font-bold text-slate-500">{l}{r.current_mode === m ? " · المعتمد حالياً" : ""}</p>
          <p className="mt-1 font-heading text-2xl font-black text-slate-900" data-testid={`sim-net-${m}`}>{r.modes[m].net.toLocaleString()} <span className="text-sm">ر.س</span></p>
          <p className="text-[11px] text-slate-500">الإجمالي {r.modes[m].gross.toLocaleString()} − الخصومات {r.total_deductions.toLocaleString()}</p>
        </div>
      ))}
    </div>
    <div className="rounded-2xl border border-slate-200 bg-white p-4 text-sm">
      <p className="mb-2 font-bold">المستوى {r.level}{r.next_level ? ` · باقي ${r.remaining} طلب للمستوى ${r.next_level}` : ""} · {r.rule ? `الصيغة المطابقة: من ${r.rule.from_orders}${r.rule.to_orders ? ` حتى ${r.rule.to_orders}` : ""} طلب` : "لا توجد صيغة مطابقة"}</p>
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-5">
        {Object.entries(r.deductions).map(([k, v]) => <div key={k} className="rounded-lg bg-rose-50 p-2 text-center"><p className="text-[11px] text-rose-700">{DED[k]}</p><p className="font-bold" data-testid={`sim-ded-${k}`}>{v}</p></div>)}
      </div>
    </div>
  </div>
);

export default function SalarySimulator() {
  const [tabs, setTabs] = useState([]);
  const [f, setF] = useState({ platform: "", orders: 450, valid: true, grade: "A", rejections: 0, cancellations: 0, face: 0, fake_gps: 0 });
  const [r, setR] = useState(null);
  useEffect(() => {
    axios.get(`${API}/salary-target/settings`).then(({ data }) => {
      const k = (data.platform_tabs || []).filter((t) => String(t.platform).includes("كيتا")).map((t) => t.platform);
      setTabs(k); if (k[0]) setF((x) => ({ ...x, platform: k[0] }));
    }).catch(() => {});
  }, []);
  const up = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const run = async () => {
    try { setR((await axios.post(`${API}/salary-target/simulate`, { ...f, orders: Number(f.orders) || 0, rejections: Number(f.rejections) || 0, cancellations: Number(f.cancellations) || 0, face: Number(f.face) || 0, fake_gps: Number(f.fake_gps) || 0 })).data); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الحساب"); }
  };
  useEffect(() => { if (f.platform) run(); }, [f]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!tabs.length) return <p className="rounded-2xl border border-dashed p-8 text-center text-sm text-slate-500" data-testid="sim-empty">أضف صفحة «راتب كيتا» في إعدادات تارقت الرواتب أولاً.</p>;
  const num = (k, l) => <F label={l}><Input type="number" value={f[k]} onChange={(e) => up(k, e.target.value)} data-testid={`sim-${k}`} /></F>;
  return (
    <div className="space-y-4" dir="rtl" data-testid="salary-simulator">
      <div className="flex items-center gap-2"><Calculator className="h-5 w-5 text-indigo-600" /><h2 className="font-heading text-lg font-bold">محاكاة راتب كيتا</h2></div>
      <p className="text-xs text-slate-500">اكتب الأرقام وشاهد فوراً الراتب في كل نموذج حسب الإعدادات المحفوظة — لا يُحفظ شيء.</p>
      <div className="grid grid-cols-2 gap-3 rounded-2xl border border-slate-200 bg-white p-4 sm:grid-cols-4">
        <F label="صفحة الراتب"><select className="h-9 w-full rounded-md border px-2 text-sm" value={f.platform} onChange={(e) => up("platform", e.target.value)} data-testid="sim-platform">{tabs.map((t) => <option key={t}>{t}</option>)}</select></F>
        {num("orders", "عدد الطلبات")}
        <F label="الأداء"><select className="h-9 w-full rounded-md border px-2 text-sm" value={f.valid ? "1" : "0"} onChange={(e) => up("valid", e.target.value === "1")} data-testid="sim-valid"><option value="1">صالح</option><option value="0">غير صالح</option></select></F>
        <F label="التقييم"><select className="h-9 w-full rounded-md border px-2 text-sm" value={f.grade} onChange={(e) => up("grade", e.target.value)} data-testid="sim-grade">{["A", "B", "C", "D", "F"].map((g) => <option key={g}>{g}</option>)}</select></F>
        {num("rejections", "طلبات مرفوضة")}{num("cancellations", "طلبات ملغاة")}{num("face", "عدم تطابق Face ID")}{num("fake_gps", "حالات Fake GPS")}
      </div>
      <Button variant="outline" size="sm" onClick={run} data-testid="sim-run">إعادة الحساب</Button>
      {r && <Result r={r} />}
    </div>
  );
}
